import React, { useState } from "react";
import { ArrowRight, ChevronDown, ShieldAlert, Users } from "lucide-react";
import {
  DashboardAttentionEngagementClient,
  DashboardAttentionReportData,
  DashboardAttentionSafetyClient,
} from "../api/contracts";
import {
  engagementClientReason,
  engagementStatusOrder,
  engagementStatusPresentation,
} from "../config/engagementStatus";
import {
  clientProfilePath,
  teamClientDetailPath,
  teamClientsPath,
} from "../config/navigation";
import { PortalLink } from "./PortalLink";
import { buildClientUsageAnalyticsUrl } from "../pages/UsageEngagementPage";
import { formatPortalDateTime } from "../utils/dateTime";
import { ReportHelpTooltip } from "./ReportVisuals";

const PREVIEW_LIMIT = 3;
const ENGAGEMENT_PREVIEW_LIMIT = 4;

const engagementSummaryItems = engagementStatusOrder.map((status) => ({
  status,
  summaryKey:
    status === "on-track"
      ? ("clientsOnTrack" as const)
      : status === "needs-follow-up"
        ? ("clientsNeedingFollowUp" as const)
        : status === "no-recent-assistant-use"
          ? ("clientsWithNoRecentAssistantUse" as const)
          : ("newClients" as const),
  ...engagementStatusPresentation[status],
}));

function clientLabel(nickname: string, accessCode: string): string {
  return nickname.trim() || accessCode;
}

function AttentionSkeleton({ label }: { label: string }) {
  return (
    <div className="cp-attention-skeleton" role="status" aria-label={label}>
      <span className="cp-attention-skeleton__metric" />
      <span />
      <span />
      <span />
    </div>
  );
}

function SafetyClientRow({
  client,
}: {
  client: DashboardAttentionSafetyClient;
}) {
  return (
    <li className="cp-attention-client">
      <div className="cp-attention-client__identity">
        <strong>{clientLabel(client.nickname, client.accessCode)}</strong>
        <span>{client.accessCode}</span>
      </div>
      <p>
        {client.safetyDetectionCount}{" "}
        {client.safetyDetectionCount === 1 ? "detection" : "detections"}
        <span aria-hidden="true"> · </span>
        {client.sessionsWithSafetyDetections}{" "}
        {client.sessionsWithSafetyDetections === 1 ? "session" : "sessions"}
      </p>
      <p className="cp-attention-client__date">
        Most recent {formatPortalDateTime(client.lastSafetyEventAt)}
      </p>
      <a
        className="cp-attention-client__action"
        href={buildClientUsageAnalyticsUrl(
          client.accessCode,
          client.nickname,
          "last_7_days",
          "safety",
        )}
        target="_blank"
        rel="noopener noreferrer"
      >
        Review Safety Events <ArrowRight aria-hidden="true" />
      </a>
    </li>
  );
}

function EngagementClientRow({
  client,
  clinicianCode,
  scope,
}: {
  client: DashboardAttentionEngagementClient;
  clinicianCode: string;
  scope: "personal" | "team";
}) {
  const label = clientLabel(client.nickname, client.accessCode);
  return (
    <li
      className="cp-attention-client cp-engagement-client"
      data-engagement-status={client.engagementStatus}
    >
      <div className="cp-attention-client__identity">
        <span className="cp-engagement-client__title">
          <span className="cp-engagement-status-dot" aria-hidden="true" />
          <strong>{label}</strong>
          <span aria-hidden="true">—</span>
          <span className="cp-engagement-client__status">
            {engagementStatusPresentation[client.engagementStatus].label}
          </span>
        </span>
        <span>{client.accessCode}</span>
      </div>
      <p className="cp-engagement-client__reason">
        {engagementClientReason(client)}
      </p>
      <PortalLink
        className="cp-attention-client__action"
        path={
          scope === "team"
            ? teamClientDetailPath(client.clientId)
            : clientProfilePath(client.clientId)
        }
        clinicianCode={clinicianCode}
        aria-label={`View ${label}`}
      >
        View Client <ArrowRight aria-hidden="true" />
      </PortalLink>
    </li>
  );
}

export function DashboardNeedsAttention({
  clinicianCode,
  report,
  loading,
  scope,
}: {
  clinicianCode: string;
  report?: DashboardAttentionReportData;
  loading: boolean;
  scope: "personal" | "team";
}) {
  const [safetyExpanded, setSafetyExpanded] = useState(false);
  const [engagementExpanded, setEngagementExpanded] = useState(false);
  const [showAllSafetyClients, setShowAllSafetyClients] = useState(false);
  const safetyClients = report?.safetyClients ?? [];
  const engagementClients = report?.engagementClients ?? [];
  const visibleSafetyClients = showAllSafetyClients
    ? safetyClients
    : safetyClients.slice(0, PREVIEW_LIMIT);
  const visibleEngagementClients = engagementClients.slice(
    0,
    ENGAGEMENT_PREVIEW_LIMIT,
  );
  const hiddenSafetyCount = safetyClients.length - PREVIEW_LIMIT;
  const hiddenEngagementCount = Math.max(
    engagementClients.length - ENGAGEMENT_PREVIEW_LIMIT,
    0,
  );
  const safetyState = loading
    ? "loading"
    : !report
      ? "unavailable"
      : report.summary.totalSafetyDetections > 0
        ? "alert"
        : "clear";
  const safetyContext =
    safetyState === "alert"
      ? "Higher priority"
      : safetyState === "clear"
        ? "No events detected"
        : safetyState === "loading"
          ? "Checking recent activity"
          : "Status unavailable";

  const safetyCard = (
    <article
      className="cp-attention-card"
      data-tone="safety"
      data-safety-state={safetyState}
      data-expanded={safetyExpanded}
      aria-label="Safety Events · Last 7 Days"
    >
      <header className="cp-attention-card__header">
        <span className="cp-attention-card__icon" aria-hidden="true">
          <ShieldAlert />
        </span>
        <div>
          <h3>Safety Events · Last 7 Days</h3>
          <span className="cp-attention-card__priority">{safetyContext}</span>
        </div>
        {!loading && safetyClients.length ? (
          <button
            type="button"
            className="cp-attention-card__toggle"
            aria-expanded={safetyExpanded}
            aria-controls="dashboard-safety-event-details"
            aria-label={`${safetyExpanded ? "Collapse" : "Expand"} Safety Events details`}
            title={`${safetyExpanded ? "Collapse" : "Expand"} Safety Events details`}
            onClick={() => setSafetyExpanded((expanded) => !expanded)}
          >
            <ChevronDown aria-hidden="true" />
          </button>
        ) : null}
      </header>
      {loading ? (
        <AttentionSkeleton label="Loading safety events" />
      ) : safetyClients.length ? (
        <>
          <div className="cp-attention-card__summary">
            <strong>{report?.summary.clientsWithSafetyEvents}</strong>
            <span>clients with safety detections</span>
            <small>
              {report?.summary.totalSafetyDetections} total detections across{" "}
              {report?.summary.sessionsWithSafetyDetections}{" "}
              {report?.summary.sessionsWithSafetyDetections === 1
                ? "session"
                : "sessions"}
            </small>
          </div>
          {safetyExpanded ? (
            <div
              id="dashboard-safety-event-details"
              className="cp-attention-card__details"
            >
              <ul className="cp-attention-card__clients">
                {visibleSafetyClients.map((client) => (
                  <SafetyClientRow key={client.clientId} client={client} />
                ))}
              </ul>
              {hiddenSafetyCount > 0 ? (
                <button
                  type="button"
                  className="cp-attention-more"
                  onClick={() => setShowAllSafetyClients((visible) => !visible)}
                >
                  {showAllSafetyClients
                    ? "Show fewer"
                    : `+ ${hiddenSafetyCount} more`}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <p className="cp-attention-card__empty">
          No safety events detected in the last 7 days.
        </p>
      )}
    </article>
  );
  const engagementCard = (
    <article
      className="cp-attention-card"
      data-tone="engagement"
      data-expanded={engagementExpanded}
      aria-label="Caseload Engagement · At a Glance"
    >
      <header className="cp-attention-card__header">
        <span className="cp-attention-card__icon" aria-hidden="true">
          <Users />
        </span>
        <div>
          <h3>Caseload Engagement · At a Glance</h3>
          <span className="cp-attention-card__context">
            7-day assistant use · all-time homework completion
          </span>
        </div>
        {!loading && engagementClients.length ? (
          <button
            type="button"
            className="cp-attention-card__toggle"
            aria-expanded={engagementExpanded}
            aria-controls="dashboard-engagement-client-details"
            aria-label={`${engagementExpanded ? "Collapse" : "Expand"} Caseload Engagement details`}
            title={`${engagementExpanded ? "Collapse" : "Expand"} Caseload Engagement details`}
            onClick={() => setEngagementExpanded((expanded) => !expanded)}
          >
            <ChevronDown aria-hidden="true" />
          </button>
        ) : null}
      </header>
      {loading ? (
        <AttentionSkeleton label="Loading caseload engagement" />
      ) : report ? (
        <>
          <ul
            className="cp-engagement-summary"
            aria-label="Caseload engagement summary"
          >
            {engagementSummaryItems.map((item) => {
              const count = report.summary[item.summaryKey];
              const destinationLabel =
                item.status === "new-client"
                  ? "New Clients"
                  : `${item.label} clients`;
              const summaryContent = (
                <>
                  <span
                    className="cp-engagement-status-dot"
                    aria-hidden="true"
                  />
                  <strong>{count}</strong>
                  <span className="cp-engagement-summary__label">
                    {item.label}
                  </span>
                </>
              );
              return (
                <li
                  key={item.status}
                  data-engagement-status={item.status}
                  data-has-clients={count > 0}
                >
                  {count > 0 ? (
                    <PortalLink
                      className="cp-engagement-summary__content cp-engagement-summary__link"
                      path={`${
                        scope === "team"
                          ? teamClientsPath
                          : "/clinician-portal/clients"
                      }?signal=${encodeURIComponent(item.status)}`}
                      clinicianCode={clinicianCode}
                      aria-label={`View ${destinationLabel} (${count})`}
                    >
                      {summaryContent}
                    </PortalLink>
                  ) : (
                    <span className="cp-engagement-summary__content">
                      {summaryContent}
                    </span>
                  )}
                  <ReportHelpTooltip
                    label={item.label}
                    definition={item.definition}
                    placement={
                      item.status === "no-recent-assistant-use" ||
                      item.status === "new-client"
                        ? "top"
                        : "bottom"
                    }
                  />
                </li>
              );
            })}
          </ul>
          {engagementExpanded && engagementClients.length ? (
            <div
              id="dashboard-engagement-client-details"
              className="cp-attention-card__details"
            >
              <ul className="cp-attention-card__clients">
                {visibleEngagementClients.map((client) => (
                  <EngagementClientRow
                    key={client.clientId}
                    client={client}
                    clinicianCode={clinicianCode}
                    scope={scope}
                  />
                ))}
              </ul>
              {hiddenEngagementCount > 0 ? (
                <p className="cp-engagement-client__more">
                  {hiddenEngagementCount} more{" "}
                  {hiddenEngagementCount === 1 ? "client" : "clients"}
                </p>
              ) : null}
            </div>
          ) : null}
          {!engagementClients.length ? (
            <p className="cp-attention-card__empty">
              {scope === "team"
                ? "No activated team clients to evaluate."
                : "No activated clients to evaluate."}
            </p>
          ) : null}
          <PortalLink
            className="cp-attention-card__footer-action"
            path={
              scope === "team" ? teamClientsPath : "/clinician-portal/clients"
            }
            clinicianCode={clinicianCode}
          >
            {scope === "team" ? "View Team Clients" : "View Clients"}{" "}
            <ArrowRight aria-hidden="true" />
          </PortalLink>
        </>
      ) : (
        <p className="cp-attention-card__empty">
          Caseload engagement is unavailable.
        </p>
      )}
    </article>
  );

  return (
    <div className="cp-attention-grid">
      {engagementCard}
      {safetyCard}
    </div>
  );
}
